import { useMutation } from '@apollo/client';
import {
  ErrorBoundary,
  SuspenseLoader,
  useAppUser,
  useBlockPermission,
  useWeldAssignees,
  useWeldOwner,
  useWorkPackageOwner,
  WeldMilestoneType,
} from '@bechtel/client-common';
import { CLAIM_MILESTONE, UNCLAIM_MILESTONE, UNCLAIM_WELD } from '@bechtel/client-gql';
import {
  useApproveFitup,
  useApproveWeld,
  useUnapproveFitup,
  useUnApproveWeld,
  useWeldPermissions,
  WeldActionError,
  WeldActionErrorExt,
} from '@bechtel/client-weld-common';
import { Box, Icon, IconSize, Text, useModalState } from '@bechtel/gds';
import {
  ClaimMilestoneInput,
  ClaimMilestonePayload,
  CommodityAssignee,
  CommodityStatusCode,
  MilestoneStatusCode,
  MilestoneStatusInput,
  UnclaimMilestonePayload,
  UnclaimWeldPayload
} from '@bechtel/types';
import { t } from '@lingui/macro';
import { addDays, isBefore, parseISO } from 'date-fns';
import { find, get } from 'lodash';
import { FC, Fragment, Suspense, useState } from 'react';
import { ConfirmClaimAction } from '~/components/molecules/confirm';
import { useWorkPackage } from '~/detail/context';
import { useWorkPackagePermissions } from '~/hooks';
import { ActionSpinner } from '../../../action';
import { MilestoneError } from '../../../error';
import { WeldMilestoneActionStyled } from '../styled';
import { WeldMilestoneActionContentProps, WeldMilestoneActionProps, WeldMilestoneNonActionProps } from '../types';

export const ActionContent: FC<WeldMilestoneActionContentProps> = ({ icon, label }) => {
  return (
    <Fragment>
      <Box mr={{ sm: '400' }}>
        <Icon icon={icon} size={IconSize.MEDIUM} />
      </Box>
      <Text color="grey.1000" size="300">
        {label}
      </Text>
    </Fragment>
  );
};

export const UnclaimWeldAction: FC<WeldMilestoneActionProps> = ({ disabled, status, milestone }) => {
  const [unclaimWeldError, SetUnlaimWeldError] = useState<WeldActionErrorExt | null>();
  const [unclaimWeld, { loading: mutationLoading }] = useMutation<
    { unclaimWeldPayload: UnclaimWeldPayload },
    { input: MilestoneStatusInput }
  >(UNCLAIM_WELD, {
    onCompleted: (data) => {
      const error = get(data, 'unclaimWeld.error');
      if (error) {
        SetUnlaimWeldError(error);
      } else {
        SetUnlaimWeldError(null);
        close();
      }
    },
    onError: (error) => {
      SetUnlaimWeldError(error);
    },
    refetchQueries: ['commodityStatsSummary'],
  });
  const closeModal = () => {
    SetUnlaimWeldError(null);
  };
  return (
    <>
      <WeldMilestoneActionStyled
        status={status}
        disabled={mutationLoading || disabled}
        onClick={async () => {
          await unclaimWeld({
            variables: { input: { commodityMilestoneId: milestone.id } },
          });
        }}
      >
        {mutationLoading && <ActionSpinner label={t`Unclaim`} />}
        {!mutationLoading && <ActionContent label={t`Unclaim`} icon="minus" />}
      </WeldMilestoneActionStyled>
      {unclaimWeldError && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader />}>
            <WeldActionError error={unclaimWeldError} close={closeModal} />
          </Suspense>
        </ErrorBoundary>
      )}
    </>
  );
};

export const WeldSendBackAction: FC<WeldMilestoneActionProps> = (props) => {
  const { disabled, status, milestone, setMilestoneData } = props;

  return (
    <WeldMilestoneActionStyled
      status={status}
      disabled={disabled}
      onClick={() => {
        if (setMilestoneData) {
          setMilestoneData(milestone);
        }
      }}
    >
      <ActionContent label={t`Send Back`} icon="minus" />
    </WeldMilestoneActionStyled>
  );
};

export const ApproveWeldAction: FC<WeldMilestoneActionProps> = ({ disabled, status, milestone }) => {
  const [approveWeldError, setApproveWeldError] = useState<WeldActionErrorExt | null>();
  const onCompleted = (data) => {
    const error = get(data, 'approveWeld.error');
    if (error) {
      setApproveWeldError(error);
    } else {
      setApproveWeldError(null);
      close();
    }
  };
  const onError = (error) => {
    setApproveWeldError(error);
  };
  const [approveWeld, { loading }] = useApproveWeld(onCompleted, onError, ['commodityStatsSummary']);

  const closeModal = () => {
    setApproveWeldError(null);
  };
  return (
    <Fragment>
      <WeldMilestoneActionStyled
        status={status}
        disabled={loading || disabled}
        onClick={async () => {
          await approveWeld({
            variables: { input: { commodityMilestoneId: milestone.id } },
          });
        }}
      >
        {loading && <ActionSpinner label={t`Approve`} />}
        {!loading && <ActionContent label={t`Approve`} icon="check" />}
      </WeldMilestoneActionStyled>
      {approveWeldError && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader />}>
            <WeldActionError error={approveWeldError} close={closeModal} />
          </Suspense>
        </ErrorBoundary>
      )}
    </Fragment>
  );
};
export const ApproveRejectWeldAction: FC<WeldMilestoneActionProps> = (props) => {
  const { weld } = props;
  const assignedCrewMembers: CommodityAssignee[] = useWeldAssignees(weld?.commodityAssignees);
  const { canApproveWeld, canRejectWeld } = useWeldPermissions(assignedCrewMembers);

  return (
    <Fragment>
      {canRejectWeld && <WeldSendBackAction {...props} />}&nbsp;&nbsp;
      {canApproveWeld && <ApproveWeldAction {...props} />}
    </Fragment>
  );
};
export const ApproveUnclaimWeldAction: FC<WeldMilestoneActionProps> = (props) => {
  const { weld } = props;
  const assignedCrewMembers: CommodityAssignee[] = useWeldAssignees(weld?.commodityAssignees);
  const { canApproveWeld, canClaimWeld } = useWeldPermissions(assignedCrewMembers);
  return (
    <Fragment>
      {canClaimWeld && <UnclaimWeldAction {...props} />}&nbsp;&nbsp;
      {canApproveWeld && <ApproveWeldAction {...props} />}
    </Fragment>
  );
};

export const ApproveFitupAction: FC<WeldMilestoneActionProps> = ({ status, disabled, milestone }) => {
  const [approveFitupError, setApproveFitupError] = useState<WeldActionErrorExt | null>();
  const onCompleted = (data) => {
    const error = get(data, 'approveFitup.error');
    if (error) {
      setApproveFitupError(error);
    } else {
      setApproveFitupError(null);
      close();
    }
  };
  const onError = (error) => {
    setApproveFitupError(error);
  };
  const [approveFitup, { loading }] = useApproveFitup(onCompleted, onError, ['commodityStatsSummary']);

  return (
    <>
      {approveFitupError && <MilestoneError milestoneError={approveFitupError}></MilestoneError>}
      <WeldMilestoneActionStyled
        status={status}
        onClick={async () => {
          await approveFitup({
            variables: { input: { commodityMilestoneId: milestone.id } },
          });
        }}
        disabled={loading || disabled}
      >
        {loading && <ActionSpinner label={t`Approve`}></ActionSpinner>}
        {!loading && <ActionContent label={t`Approve`} icon="check" />}
      </WeldMilestoneActionStyled>
    </>
  );
};

export const ApproveRejectFitupAction: FC<WeldMilestoneActionProps> = (props) => {
  const { weld } = props;
  const assignedCrewMembers: CommodityAssignee[] = useWeldAssignees(weld?.commodityAssignees);
  const { canApproveFitup, canRejectFitup } = useWeldPermissions(assignedCrewMembers);
  return (
    <Fragment>
      {canRejectFitup && <WeldSendBackAction {...props} />}&nbsp;&nbsp;
      {canApproveFitup && <ApproveFitupAction {...props} />}
    </Fragment>
  );
};

export const UnapproveWeldAction: FC<WeldMilestoneActionProps> = (props) => {
  const { status, milestone } = props;
  const [unApproveWeldError, setUnApproveWeldError] = useState<WeldActionErrorExt | null>();
  const onCompleted = (data) => {
    const error = get(data, 'unApproveWeld.error');
    if (error) {
      setUnApproveWeldError(error);
    } else {
      setUnApproveWeldError(null);
      close();
    }
  };
  const onError = (error) => {
    setUnApproveWeldError(error);
  };
  const [unApproveWeld, { loading }] = useUnApproveWeld(onCompleted, onError, ['commodityStatsSummary']);

  const closeModal = () => {
    setUnApproveWeldError(null);
  };
  return (
    <>
      <WeldMilestoneActionStyled
        disabled={loading}
        status={status}
        onClick={async () => {
          await unApproveWeld({
            variables: { input: { commodityMilestoneId: milestone.id } },
          });
        }}
      >
        {loading && <ActionSpinner label={t`Unapprove`} />}
        {!loading && <ActionContent label={t`Unapprove`} icon="minus" />}
      </WeldMilestoneActionStyled>
      {unApproveWeldError && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <WeldActionError error={unApproveWeldError} close={closeModal} />
          </Suspense>
        </ErrorBoundary>
      )}
    </>
  );
};

export const UnapproveFitupAction: FC<WeldMilestoneActionProps> = (props) => {
  const { status, milestone } = props;
  const [unApproveFitupError, setUnApproveFitupError] = useState<WeldActionErrorExt | null>();
  const onCompleted = (data) => {
    const error = get(data, 'unapproveFitup.error');
    if (error) {
      setUnApproveFitupError(error);
    } else {
      setUnApproveFitupError(null);
      close();
    }
  };
  const onError = (error) => {
    setUnApproveFitupError(error);
  };
  const [unapproveFitup, { loading }] = useUnapproveFitup(onCompleted, onError, ['commodityStatsSummary']);

  const closeModal = () => {
    setUnApproveFitupError(null);
  };
  return (
    <>
      <WeldMilestoneActionStyled
        disabled={loading}
        status={status}
        onClick={async () => {
          await unapproveFitup({
            variables: { input: { commodityMilestoneId: milestone.id } },
          });
        }}
      >
        {loading && <ActionSpinner label={t`Unapprove`} />}
        {!loading && <ActionContent label={t`Unapprove`} icon="minus" />}
      </WeldMilestoneActionStyled>
      {unApproveFitupError && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <WeldActionError error={unApproveFitupError} close={closeModal} />
          </Suspense>
        </ErrorBoundary>
      )}
    </>
  );
};

export const UnclaimAction: FC<WeldMilestoneActionProps> = ({ status, disabled, milestone }) => {
  const [unClaimMilestone, { loading, data, error }] = useMutation<
    { unclaimMilestone: UnclaimMilestonePayload },
    { input: MilestoneStatusInput }
  >(UNCLAIM_MILESTONE, {
    variables: {
      input: {
        commodityMilestoneId: milestone.id,
      },
    },
    refetchQueries: ['commodityStatsSummary'],
  });
  const milestoneError = get(data, 'unclaimMilestone.error') || error;

  return (
    <>
      {milestoneError && <MilestoneError milestoneError={milestoneError}></MilestoneError>}
      <WeldMilestoneActionStyled status={status} onClick={() => unClaimMilestone()} disabled={loading || disabled}>
        {loading && <ActionSpinner label={t`Unclaim`}></ActionSpinner>}
        {!loading && <ActionContent label={t`Unclaim`} icon={disabled ? 'lock' : 'minus'} />}
      </WeldMilestoneActionStyled>
    </>
  );
};

export const ClaimAction: FC<WeldMilestoneActionProps> = ({ status, milestone, actionLoading, showItrCertDialog}) => {
  const [claimMilestone, { loading, data, error }] = useMutation<
    { claimMilestone: ClaimMilestonePayload },
    { input: ClaimMilestoneInput }
  >(CLAIM_MILESTONE, {
    variables: {
      input: {
        commodityMilestoneId: milestone.id,
        itrRequired: showItrCertDialog,
      },
    },
    refetchQueries: ['commodityStatsSummary'],
  });
  const { visible, open, close } = useModalState(false);

  const milestoneError = get(data, 'claimMilestone.error') || error;

  const onCertifyClick = async() => {
    claimMilestone();
  };

  return (
    <>
      {visible && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <ConfirmClaimAction onCertifyClick={onCertifyClick} isMutationInProgress={loading} close={close} />
          </Suspense>
        </ErrorBoundary>
      )}
      {milestoneError && <MilestoneError milestoneError={milestoneError}></MilestoneError>}
      <WeldMilestoneActionStyled
        status={status}
        onClick={() => {
          if (showItrCertDialog) {
            open();
          } else {
            claimMilestone();
          }
        }}
        disabled={loading || actionLoading}
      >
        {loading ? <ActionSpinner label={t`Claim`}></ActionSpinner> : <ActionContent label={t`Claim`} icon="plus" />}
      </WeldMilestoneActionStyled>
    </>
  );
};

export const ReclaimAction: FC<WeldMilestoneActionProps> = (props) => {
  const { disabled, status, setMilestoneData, milestone } = props;
  return (
    <WeldMilestoneActionStyled
      disabled={disabled}
      status={status}
      onClick={() => {
        if (!setMilestoneData) return;
        setMilestoneData(milestone);
      }}
    >
      <ActionContent label={t`Reclaim`} icon="undo" />
    </WeldMilestoneActionStyled>
  );
};

export const ClaimWeldAction: FC<WeldMilestoneActionProps> = (props) => {
  const { disabled, status, setMilestoneData, milestone } = props;
  return (
    <WeldMilestoneActionStyled
      disabled={disabled}
      status={status}
      onClick={() => {
        if (!setMilestoneData) return;
        setMilestoneData(milestone);
      }}
      isAssigned={props?.isAssigned}
    >
      <ActionContent label={t`Claim`} icon="plus" />
    </WeldMilestoneActionStyled>
  );
};

export const ReclaimWeldAction: FC<WeldMilestoneActionProps> = (props) => {
  const { disabled, status, milestone, setMilestoneData } = props;
  return (
    <WeldMilestoneActionStyled
      disabled={disabled}
      status={status}
      onClick={() => {
        if (!setMilestoneData) return;
        setMilestoneData(milestone);
      }}
    >
      <ActionContent label={t`Reclaim`} icon="undo" />
    </WeldMilestoneActionStyled>
  );
};

export const ResolveBlockAction: FC<WeldMilestoneActionProps> = ({ status, setMilestoneData, milestone }) => {
  return (
    <WeldMilestoneActionStyled status={status} onClick={() => setMilestoneData && setMilestoneData(milestone)}>
      <ActionContent label={t`Resolve block`} icon="exclamation-triangle" />
    </WeldMilestoneActionStyled>
  );
};

const NonAction: FC<WeldMilestoneNonActionProps> = ({ icon, label }) => {
  return (
    <Box alignItems={{ sm: 'center' }} justifyContent={{ sm: 'center' }} p={{ sm: '400' }} flexGrow={{ sm: 1 }}>
      <Box mr={{ sm: '400' }}>
        <Icon icon={icon} />
      </Box>
      <Text size="300" color="grey.1000">
        {label}
      </Text>
    </Box>
  );
};

export const WeldMilestoneAction: FC<WeldMilestoneActionProps> = (props) => {
  const { assignedCrewMembers: workPackageOwners, facilities } = useWorkPackage();
  const { user } = useAppUser();
  const isWorkPackageOwner = useWorkPackageOwner(workPackageOwners);
  const { canClaimMilestone } = useWorkPackagePermissions(workPackageOwners);
  const { milestone, status, weld, isAssigned, isFitupRequired } = props;
  const isFitupMilestone = milestone?.name?.toUpperCase() === WeldMilestoneType.FITUP;
  const { canResolveBlock } = useBlockPermission(
    workPackageOwners,
    facilities ? facilities : [],
    null,
    weld?.commodityAssignees
  );
  const assignedCrewMembers: CommodityAssignee[] = useWeldAssignees(weld?.commodityAssignees);
  const { canClaimWeld, canApproveWeld, canRejectWeld, canApproveFitup, canRejectFitup } =
    useWeldPermissions(assignedCrewMembers);
  const isCommodityOwner = useWeldOwner(assignedCrewMembers);
  const createdByUserId = get(milestone, 'currentStatus.createdBy.id');
  const createdAt = get(milestone, 'currentStatus.createdAt');
  const parsedCreatedAt = createdAt ? parseISO(createdAt) : undefined;
  const currentProjectWeek = find(user?.project?.projectConfiguration?.projectWeeks, (projectWeek) =>
    projectWeek?.isCurrent ? true : false
  );
  const currentWeekStartDate = parseISO(currentProjectWeek?.startDate);
  const isClaimer = status === MilestoneStatusCode.CLAIMED && user && createdByUserId === user?.id;
  const unclaimProps = {
    ...props,
    disabled:
      (isWorkPackageOwner || isCommodityOwner) &&
      createdAt &&
      isBefore(new Date(createdAt), new Date(addDays(new Date(), -2).toISOString())),
  };
  const commodityStatus = weld.status?.status;

  switch (status) {
    case MilestoneStatusCode.REQUESTED:
      if (canClaimMilestone) return <ClaimAction {...props} />;
      return <NonAction icon="check" label={t`Requested`} />;
    case MilestoneStatusCode.PLANNED:
      if (canClaimWeld && isAssigned) return <ClaimWeldAction {...props} />;
      return null;
    case MilestoneStatusCode.UNPLANNED:
      if (canClaimWeld && isAssigned) return <ClaimWeldAction {...props} />;
      return null;
    case MilestoneStatusCode.CLAIMED:
      if (isClaimer) {
        // CLAIMED -> UNCLAIM
        // claimed by user and hence can always unclaim!
        if (commodityStatus === CommodityStatusCode.FITUP_INSPECTION) {
          if (canClaimMilestone) return <UnclaimAction {...unclaimProps} />;
          if (canApproveFitup || canRejectFitup) return <ApproveRejectFitupAction {...props} />;
        }
        if (commodityStatus === CommodityStatusCode.CLAIMED) {
          if (canClaimWeld && !canApproveWeld) return <UnclaimWeldAction {...unclaimProps} />;
          if (canClaimWeld && canApproveWeld) return <ApproveUnclaimWeldAction {...unclaimProps} />;
        }
        <NonAction icon="check" label={t`Claimed`} />;
      } else if (!isClaimer) {
        // CLAIMED -> APPROVE / REJECT
        // Other user claimed, user gets option to Reject / Approve
        if (commodityStatus === CommodityStatusCode.FITUP_INSPECTION && (canApproveFitup || canRejectFitup))
          return <ApproveRejectFitupAction {...props} />;
        if (commodityStatus === CommodityStatusCode.CLAIMED && (canApproveWeld || canRejectWeld))
          return <ApproveRejectWeldAction {...props} />;
      } else if (isWorkPackageOwner || isCommodityOwner) {
        if (commodityStatus === CommodityStatusCode.FITUP_INSPECTION) return <UnclaimAction {...unclaimProps} />;
        else if (commodityStatus === CommodityStatusCode.CLAIMED) return <UnclaimWeldAction {...unclaimProps} />;
      }
      return <NonAction icon="check" label={t`Claimed`} />;
    case MilestoneStatusCode.APPROVED:
      if (user?.id === createdByUserId && parsedCreatedAt && !isBefore(parsedCreatedAt, currentWeekStartDate)) {
        if (commodityStatus === CommodityStatusCode.READY_TO_WELD && canApproveFitup)
          return <UnapproveFitupAction {...props} />;
        else if (commodityStatus === CommodityStatusCode.VT_APPROVED && canApproveWeld)
          return <UnapproveWeldAction {...props} />;
      }
      // NO ACTION: display only
      return <NonAction icon="check" label={t`Approved`} />;
    case MilestoneStatusCode.REJECTED:
      // REJECTED -> CLAIM
      if (
        canClaimMilestone &&
        commodityStatus === CommodityStatusCode.SENT_BACK_TO_PIPING &&
        weld?.fitupRequest?.isFitupRequired
      )
        return <ReclaimAction {...props} />;
      if (canClaimWeld && commodityStatus === CommodityStatusCode.VT_SENT_BACK) return <ReclaimWeldAction {...props} />;
      // NO ACTION: display only
      if (!isFitupMilestone || (isFitupMilestone && isFitupRequired))
        return <NonAction icon="times" label={t`Rejected`} />; // display only
      return null;
    case MilestoneStatusCode.BLOCKED:
      if (canResolveBlock) return <ResolveBlockAction {...props} />;
      return <NonAction icon="times" label={t`Blocked`} />; // display only
    default:
      return null;
  }
};
